import { useGLTF } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { a, useSpring } from "@react-spring/three";
import * as THREE from "three";
import { Toy } from "../src/interactions/Toy";

const DEFAULT_RADIUS = 4;
const DEFAULT_ROTATION_SPEED = Math.PI / 4;
const DEFAULT_FLOAT_AMPLITUDE = 0.45;
const DEFAULT_FLOAT_SPEED = 1.2;

export default function ToyCollectible({
	id,
	modelPath,
	position = [0, 0, 0],
	radius = DEFAULT_RADIUS,
	targetRef = null,
	rotationSpeed = DEFAULT_ROTATION_SPEED,
	floatAmplitude = DEFAULT_FLOAT_AMPLITUDE,
	floatSpeed = DEFAULT_FLOAT_SPEED,
}) {
	const groupRef = useRef(null);
	const [collected, setCollected] = useState(false);
		const [consumed, setConsumed] = useState(false);

	const { scene } = useGLTF(modelPath);
		const clonedScene = useMemo(() => {
			const root = scene.clone(true);
			root.traverse((obj) => {
				if (!obj.isMesh || !obj.material) return;
				obj.material = obj.material.clone();
				obj.material.transparent = true;
				const baseOpacity = obj.material.opacity ?? 1;
				obj.material.opacity = baseOpacity;
				obj.userData.__baseOpacity = baseOpacity;
			});
			return root;
		}, [scene]);
		const outlineScene = useMemo(() => {
			const outlineRoot = scene.clone(true);
			outlineRoot.traverse((obj) => {
				if (!obj.isMesh) return;
				const outlineMaterial = new THREE.MeshBasicMaterial({
					color: 0xfff6a2,
					transparent: true,
					opacity: 0.55,
					blending: THREE.AdditiveBlending,
					depthWrite: false,
					side: THREE.BackSide,
				});
				obj.material = outlineMaterial;
				obj.scale.multiplyScalar(1.12);
				obj.renderOrder = 1000;
				obj.userData.__baseOpacity = outlineMaterial.opacity;
			});
			return outlineRoot;
		}, [scene]);

	const worldPos = useMemo(() => new THREE.Vector3(), []);
	const targetPos = useMemo(() => new THREE.Vector3(), []);
	const basePositionRef = useRef(new THREE.Vector3(position[0], position[1], position[2]));
	const elapsedRef = useRef(Math.random() * Math.PI * 2);

	const item = useMemo(
		() =>
			new Toy({
				id,
				radius,
				onCollect: () => {
					setCollected(true);
				},
			}),
		[id, radius]
	);

	useEffect(() => () => item.dispose(), [item]);

		useEffect(() => {
			basePositionRef.current.set(position[0], position[1], position[2]);
			if (groupRef.current) {
				groupRef.current.position.set(position[0], position[1], position[2]);
			}
		}, [position]);

			const setGroupOpacity = useCallback(
				(value) => {
					const applyOpacity = (root) => {
						root.traverse((obj) => {
							if (!obj.isMesh || !obj.material) return;
							const baseOpacity = obj.userData.__baseOpacity ?? 1;
							obj.material.opacity = Math.max(0, Math.min(1, baseOpacity * value));
							obj.material.needsUpdate = true;
						});
					};

					if (clonedScene) applyOpacity(clonedScene);
					if (outlineScene) applyOpacity(outlineScene);
				},
				[clonedScene, outlineScene]
			);

			useEffect(() => {
				setGroupOpacity(1);
			}, [setGroupOpacity]);

				const springs = useSpring({
					scale: collected ? 0 : 1,
					opacity: collected ? 0 : 1,
					config: { tension: 240, friction: 32 },
					onChange: ({ value }) => {
						if (typeof value.opacity === "number") {
							setGroupOpacity(value.opacity);
						}
					},
					onRest: () => {
						if (collected) {
							setConsumed(true);
						}
					},
				});

	useFrame((_, delta) => {
				if (consumed) return;

		const group = groupRef.current;
		if (!group) return;

			elapsedRef.current += delta * floatSpeed;
			const floatOffset = Math.sin(elapsedRef.current) * floatAmplitude;

			const basePos = basePositionRef.current;
			group.position.set(basePos.x, basePos.y + floatOffset, basePos.z);

			group.rotation.y += rotationSpeed * delta;

		group.getWorldPosition(worldPos);

		const targetGroup = targetRef?.current?.group;
		const translation = targetGroup?.translation?.();
		if (!translation) return;

		targetPos.set(translation.x, translation.y, translation.z);
		const distance = worldPos.distanceTo(targetPos);
		item.updateDistance(distance);
	});

			if (consumed) {
		return null;
	}

	return (
				<a.group
					ref={groupRef}
					position={position}
					scale={springs.scale.to((s) => [s * 1.5, s * 1.5, s * 1.5])}
					name={id}
				>
					<primitive object={clonedScene} dispose={null} />
					<primitive object={outlineScene} dispose={null} />
				</a.group>
	);
}
